import { compose, propOr, join, map, identity, split, omit } from 'rambda';

export const makeLocalUserUniqueId = (user: CF.LocalUser): string => user.user_id;

export const makeUserFullName = (user: CF.API.Users.User | CF.LocalUserWithOrg): string =>
  `${propOr('', 'first_name')(user)} ${propOr('', 'last_name')(user)}`.trim();

export const makeUserInitials = (user: CF.API.Users.User | CF.LocalUserWithOrg): string =>
  compose(join(''), map(identity((val) => val.charAt(0))), split(' '))(makeUserFullName(user));

export const getUserPrimaryEmail = (user: CF.API.Users.User): CF.API.Users.EmailAddressesEntity => {
  return user.email_addresses?.find((email) => email.primary) as CF.API.Users.EmailAddressesEntity;
};

export const isUserVerified = (user: CF.API.Users.User): boolean => {
  return user.email_addresses?.find((email) => email.primary)?.verified || false;
};

export const isOnboardingPending = (user: CF.API.Users.User): boolean => {
  const requiredUserInfo = ['id', 'first_name', 'last_name', 'job_role', 'date_gdpr_consent', 'date_tos_consent'];
  return isUserVerified(user) && !requiredUserInfo.every((key) => Object.keys(user).includes(key));
};

export const doesUserHaveWorkspace = (authData?: CF.LocalUserWithOrgUserAndPlan, workspaceId?: string): boolean => {
  const workspaceList = authDataToWorkspaceList(authData);
  const workspace = workspaceList.find(({ user_id }) => user_id === workspaceId);
  return Boolean(workspace);
};

export const authDataToWorkspaceList = (authData?: CF.LocalUserWithOrgUserAndPlan): CF.LocalUser[] => {
  if (!authData) {
    return [];
  }

  const loggedInUserWorkspace: CF.LocalUser = omit(['current_user_id', 'organizations'], authData);

  const orgWorkspaces: CF.LocalUser[] =
    authData?.organizations?.map(({ organization }) => ({
      user_id: organization.id,
      first_name: organization.name,
      last_name: '',
      email: organization.billing_email,
    })) || [];

  return [loggedInUserWorkspace, ...orgWorkspaces];
};
